import { useMutation, useQuery } from '@apollo/client';
import {
  ILlmConfigFieldsFragment,
  ILlmConfigQuery,
  ILlmConfigQueryVariables,
  ILlmUpdateApiConfigMutation,
  ILlmUpdateApiConfigMutationVariables,
} from '../../../../utils/apollo/graphqlSchema';
import { LLM_UPDATE_API_CONFIG } from '../../../../utils/apollo/llm/mutations';
import { LLM_CONFIG } from '../../../../utils/apollo/llm/queries';
import React, { useCallback, useState } from 'react';
import {
  FieldLabel,
  TextAreaField,
  VerticalField,
} from '../../../../components/Form';
import { SecondaryButton } from '../../../../components/Button';
import printGraphqlErrors from '../../../../utils/apollo/printGraphqlErrors';
import toastMessage from '../../../../utils/toastMessage';
import { Loader } from '../../../../components/Loaders';
import { freeze } from '@reduxjs/toolkit';
import SwitchiOS from '../../../../components/SwitchiOS';
import { assertNotNil } from '../../../../utils/types';
import {
  BoldedFieldLabel,
  ButtonsRowWrapper,
  FormWrapper,
  SettingsRow,
  StyledInputField,
  TosBodyWrapper,
  TosHeaderWrapper,
  TosWrapper,
  WiderPrimaryButton,
} from './LlmSettingsFormUI';
import { isNil } from 'lodash';
import { useAppDispatch } from '../../../../redux/hooks';
import { updateLlmApiEnabled } from '../../../../redux/slices/bootstrap';
import { useShowAiDebug } from '../../../../utils/hooks';
import { Link } from 'react-router-dom';
import LumosIconAndWordmark from '../../../../components/LumosIconAndWordmark';

const disabledLlmConfig = freeze<ILlmConfigFieldsFragment>({
  __typename: 'LlmConfig',
  enabled: false,
  projectId: null,
  organizationId: null,
  modelName: '',
  assistantInstructions: null,
  defaultAssistantInstructions: null,
});

// TODO expose projectId and organizationId in UI
const LlmSettingsForm = () => {
  const dispatch = useAppDispatch();
  const [apiKey, setApiKey] = useState('');
  const [alreadyEnabled, setAlreadyEnabled] = useState(false);
  const [originalModelName, setOriginalModelName] = useState('');
  const [originalInstructions, setOriginalInstructions] = useState<
    string | null
  >(null);
  const [llmConfig, setLlmConfig] = useState(disabledLlmConfig);
  const showInstructions = useShowAiDebug();

  const toggleEnabled = useCallback(() => {
    setLlmConfig((prev) => {
      return {
        ...prev,
        enabled: !prev.enabled,
      };
    });
  }, []);

  const setModelName = useCallback((modelName: string) => {
    setLlmConfig((prev) => {
      return {
        ...prev,
        modelName,
      };
    });
  }, []);

  const handleApiKeyChange = useCallback(
    (newKey: string) => {
      if (!!newKey && !llmConfig.enabled) {
        toggleEnabled();
      }
      setApiKey(newKey);
    },
    [toggleEnabled, llmConfig.enabled],
  );

  const setAssistantInstructions = useCallback(
    (assistantInstructions: string) => {
      setLlmConfig((prev) => {
        return {
          ...prev,
          assistantInstructions,
        };
      });
    },
    [],
  );

  const updateLlmConfigFromServer = useCallback(
    (llmConfig: ILlmConfigFieldsFragment) => {
      setLlmConfig(llmConfig);
      setAlreadyEnabled(llmConfig.enabled);
      setOriginalModelName(llmConfig.modelName);
      setOriginalInstructions(llmConfig.assistantInstructions);
      dispatch(updateLlmApiEnabled(llmConfig.enabled));
    },
    [dispatch],
  );

  // TODO this query should be glued to the entire config
  //  but all this is behind a feature flag for now so may be tricky
  const { loading } = useQuery<ILlmConfigQuery, ILlmConfigQueryVariables>(
    LLM_CONFIG,
    {
      onCompleted: (data) => {
        updateLlmConfigFromServer(data.llmConfig);
      },
    },
  );

  const [updateApiConfig, { loading: updating }] = useMutation<
    ILlmUpdateApiConfigMutation,
    ILlmUpdateApiConfigMutationVariables
  >(LLM_UPDATE_API_CONFIG);

  const onUpdateClick = async () => {
    if (!apiKey && llmConfig.enabled && !alreadyEnabled) {
      toastMessage({
        message: 'Please provide an API key in order to enable AI integration',
        type: 'error',
      });
      return;
    }
    const updateResult = await updateApiConfig({
      variables: {
        input: {
          enabled: llmConfig.enabled,
          modelName: llmConfig.modelName,
          apiKey: !!apiKey ? apiKey : null,
          projectId: llmConfig.projectId,
          organizationId: llmConfig.organizationId,
          assistantInstructions: !!llmConfig.assistantInstructions?.trim()
            ? llmConfig.assistantInstructions
            : null,
        },
      },
    }).catch((err) => {
      toastMessage({
        message: `Unexpected error: ${err}`,
        type: 'error',
      });
      return null;
    });
    if (!updateResult) {
      // error already reported via toast
      return;
    }
    const resultData = updateResult.data?.llmUpdateApiConfig;
    if (printGraphqlErrors(resultData?.errors)) {
      return;
    }
    setApiKey('');
    assertNotNil(resultData?.entity);
    updateLlmConfigFromServer(resultData.entity);
    toastMessage({
      message: 'AI Config updated',
      type: 'info',
    });
  };

  const needApiKey = llmConfig.enabled && !alreadyEnabled && !apiKey;

  const canConfirm =
    (!llmConfig.enabled && alreadyEnabled) ||
    (llmConfig.enabled && !needApiKey && !!llmConfig.modelName);

  const isDirty =
    llmConfig.enabled !== alreadyEnabled ||
    (showInstructions &&
      llmConfig.assistantInstructions !== originalInstructions) ||
    llmConfig.modelName !== originalModelName ||
    !!apiKey;

  const defaultInstruction = llmConfig.defaultAssistantInstructions;

  if (loading || updating) {
    return (
      <div>
        <Loader />
      </div>
    );
  }
  return (
    <FormWrapper>
      <LumosIconAndWordmark />
      <VerticalField noMargin={true}>
        <BoldedFieldLabel>Enable AI</BoldedFieldLabel>
        <SwitchiOS isChecked={llmConfig.enabled} onClick={toggleEnabled} />{' '}
      </VerticalField>
      <TosWrapper>
        <TosHeaderWrapper>
          Training AI{' '}
          <Link to={'https://kubit.ai/terms-of-service/'} target={'_blank'}>
            Terms of Service
          </Link>
          .
        </TosHeaderWrapper>
        <TosBodyWrapper>
          Kubit does not use Customer Data to train, customize or improve
          Kubit&apos;s artificial intelligence models. Kubit&apos;s vendors and
          service providers are not authorized to process or use any Customer
          Data from Kubit Services for their purposes to train, customize or
          improve their own artificial intelligence models and any use of
          Customer Data will be solely in connection with helping Kubit provide
          and deliver the Services to Customer.
        </TosBodyWrapper>
      </TosWrapper>
      <SettingsRow>
        <VerticalField noMargin={true}>
          <FieldLabel>API Key</FieldLabel>
          <StyledInputField value={apiKey} onChange={handleApiKeyChange} />
        </VerticalField>
        <VerticalField noMargin={true}>
          <FieldLabel>Model Name</FieldLabel>
          <StyledInputField
            value={llmConfig.modelName}
            onChange={setModelName}
          />
        </VerticalField>
      </SettingsRow>
      {showInstructions && !isNil(llmConfig.assistantInstructions) && (
        <VerticalField noMargin={true}>
          <FieldLabel>Assistant Instructions:</FieldLabel>
          <TextAreaField
            maxRows={40}
            value={llmConfig.assistantInstructions}
            onChange={setAssistantInstructions}
          />
        </VerticalField>
      )}
      <ButtonsRowWrapper>
        <WiderPrimaryButton
          onClick={onUpdateClick}
          disabled={!canConfirm || !isDirty}
        >
          Update
        </WiderPrimaryButton>
        {showInstructions && !isNil(defaultInstruction) && (
          <SecondaryButton
            isOutlined={true}
            onClick={() => setAssistantInstructions(defaultInstruction)}
          >
            Reset
          </SecondaryButton>
        )}
      </ButtonsRowWrapper>
    </FormWrapper>
  );
};

export default LlmSettingsForm;
